import { TOTP_CODE_DIGITS } from '@bartizo/shared';
import QRCode from 'qrcode';
import { useEffect, useState, type ReactNode, type SyntheticEvent } from 'react';
import { totpDisable, totpEnable, totpSetup } from '../api/auth.js';
import { isApiError } from '../api/client.js';
import { useReauth } from '../auth/reauth-context.js';
import { useSession } from '../auth/session-context.js';
import { Alert } from '../ui/Alert.js';
import { Button } from '../ui/Button.js';
import { Card } from '../ui/Card.js';
import { TextField } from '../ui/Field.js';

/**
 * Enrolling and removing an authenticator app (knowledge doc section 17).
 *
 * Three states rather than a toggle: off, mid-enrollment, and on. The middle one
 * exists because the secret is issued before it is in force — enabling on the
 * secret alone would lock out anyone whose scan quietly failed, and on a fleet
 * console that is an admin locked out of their own fleet.
 */

type Stage = 'idle' | 'enrolling' | 'disabling';

export function AuthenticatorCard(): ReactNode {
  const { session, applySession } = useSession();
  const { ensureReauthenticated } = useReauth();
  const [stage, setStage] = useState<Stage>('idle');
  const [secret, setSecret] = useState<string | null>(null);
  const [uri, setUri] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const enabled = session?.user.totpEnabled === true;

  const start = async (): Promise<void> => {
    setError(null);
    setNotice(null);
    // Starting enrollment is a change to a credential, so the API wants a recent
    // re-check. Asking here means the dialog appears before the request fails.
    if (!(await ensureReauthenticated())) return;

    setBusy(true);
    try {
      const setup = await totpSetup();
      setSecret(setup.secret);
      setUri(setup.uri);
      setStage('enrolling');
    } catch (caught: unknown) {
      setError(isApiError(caught) ? caught.message : 'Could not start setup. Try again.');
    } finally {
      setBusy(false);
    }
  };

  const confirm = async (event: SyntheticEvent): Promise<void> => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      applySession(await totpEnable(code));
      reset();
      setNotice('Your authenticator app is now required when you sign in.');
    } catch (caught: unknown) {
      setError(isApiError(caught) ? caught.message : 'Could not confirm that code.');
      setBusy(false);
    }
  };

  const remove = async (event: SyntheticEvent): Promise<void> => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      applySession(await totpDisable(code));
      reset();
      setNotice('Your authenticator app has been removed.');
    } catch (caught: unknown) {
      setError(isApiError(caught) ? caught.message : 'Could not confirm that code.');
      setBusy(false);
    }
  };

  function reset(): void {
    setStage('idle');
    setSecret(null);
    setUri(null);
    setCode('');
    setBusy(false);
    setError(null);
  }

  return (
    <Card
      title="Authenticator app"
      description="A six-digit code from an app on your phone, asked for when you sign in and before high-risk actions."
      aside={
        <span className={enabled ? 'badge badge-success' : 'badge'}>{enabled ? 'On' : 'Off'}</span>
      }
    >
      {notice !== null && <Alert tone="success">{notice}</Alert>}
      {error !== null && <Alert tone="danger">{error}</Alert>}

      {stage === 'idle' && !enabled && (
        <>
          <p className="muted text-sm">
            Anyone who learns your password can reach the whole fleet. A second factor means they
            would need your phone as well.
          </p>
          <div className="row">
            <Button variant="primary" busy={busy} onClick={() => void start()}>
              Set up authenticator
            </Button>
          </div>
        </>
      )}

      {stage === 'idle' && enabled && (
        <>
          <p className="muted text-sm">
            Lost your phone? An administrator can clear it from the central machine with{' '}
            <code>bartizo-api reset-totp {session.user.email}</code>, and you can then sign in with
            your password and enroll again.
          </p>
          <div className="row">
            <Button
              variant="danger"
              onClick={() => {
                setStage('disabling');
                setCode('');
                setNotice(null);
              }}
            >
              Remove authenticator
            </Button>
          </div>
        </>
      )}

      {stage === 'enrolling' && uri !== null && secret !== null && (
        <form onSubmit={(event) => void confirm(event)} className="stack">
          <div className="enrolment">
            <QrCode value={uri} />
            <div className="stack" style={{ flex: '1 1 14rem', gap: 'var(--space-2)' }}>
              <p className="text-sm">
                Scan this with your authenticator app, or enter the key by hand:
              </p>
              <code className="secret-text">{secret}</code>
              <p className="muted text-xs">
                It is not in force until you enter a code below, so a scan that did not work cannot
                lock you out.
              </p>
            </div>
          </div>

          <TextField
            label="Code from your app"
            autoComplete="one-time-code"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={TOTP_CODE_DIGITS}
            required
            autoFocus
            value={code}
            onChange={(event) => {
              setCode(event.target.value.replace(/\D/g, ''));
            }}
          />

          <div className="row">
            <Button type="submit" variant="primary" busy={busy} busyLabel="Checking…">
              Turn it on
            </Button>
            <Button onClick={reset}>Cancel</Button>
          </div>
        </form>
      )}

      {stage === 'disabling' && (
        <form onSubmit={(event) => void remove(event)} className="stack">
          <Alert tone="warning">
            Removing it leaves your password as the only thing between someone else and this fleet.
          </Alert>
          <TextField
            label="Code from your app"
            hint="Removing it changes a credential, so it needs a code of its own."
            autoComplete="one-time-code"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={TOTP_CODE_DIGITS}
            required
            autoFocus
            value={code}
            onChange={(event) => {
              setCode(event.target.value.replace(/\D/g, ''));
            }}
          />
          <div className="row">
            <Button type="submit" variant="danger" busy={busy} busyLabel="Checking…">
              Remove it
            </Button>
            <Button onClick={reset}>Cancel</Button>
          </div>
        </form>
      )}
    </Card>
  );
}

/**
 * The QR code, rendered to SVG in the browser.
 *
 * Client-side on purpose: the secret is in the URI, and having the API draw the
 * image would put it through another request and into another cache for no gain.
 */
function QrCode({ value }: { value: string }): ReactNode {
  const [svg, setSvg] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    QRCode.toString(value, { type: 'svg', margin: 0, width: 168, errorCorrectionLevel: 'M' })
      .then((markup) => {
        if (!cancelled) setSvg(markup);
      })
      .catch(() => {
        // The key below is the fallback, and it is always shown.
        if (!cancelled) setSvg(null);
      });
    return () => {
      cancelled = true;
    };
  }, [value]);

  if (svg === null) return null;
  return (
    <div
      className="qr"
      // The markup is produced here from a URI this app just received over its own
      // authenticated API; nothing user-supplied reaches it.
      dangerouslySetInnerHTML={{ __html: svg }}
      role="img"
      aria-label="QR code for your authenticator app"
    />
  );
}
